import { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  BookOpen,
  Plus,
  Search,
  Eye,
  Edit,
  Trash2,
  Target,
  BarChart3,
  Play,
  Pause,
  X,
  Save,
  CheckCircle,
  XCircle,
  Tag
} from 'lucide-react';

interface Question {
  id: number;
  subject: string;
  topic: string;
  difficulty: string;
  questionText: string;
  questionImage?: string;
  optionA: string;
  optionB: string;
  optionC: string;
  optionD: string;
  correctAnswer: string;
  explanation?: string;
  explanationImage?: string;
  marks: number;
  negativeMarks: number;
  year?: number;
  source?: string;
  tags: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface QuestionFormData {
  subject: string;
  topic: string;
  difficulty: string;
  questionText: string;
  questionImage: string;
  optionA: string;
  optionB: string;
  optionC: string;
  optionD: string;
  correctAnswer: string;
  explanation: string;
  explanationImage: string;
  marks: number;
  negativeMarks: number;
  year: number | null;
  source: string;
  tags: string[];
}

export default function AdminQuestions() {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, sets] = useState({
    subject: 'all',
    topic: 'all',
    difficulty: 'all',
    year: 'all',
    isActive: 'all'
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
  const [showQuestionDetails, setShowQuestionDetails] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [_topics, setTopics] = useState<string[]>([]);
  const [_totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({ total: 0, active: 0, hard: 0, topics: 0 });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await adminAPI.getQuestionStats();
      if (res.data.success) setStats(res.data.data);
    } catch {
      // stats stay at 0
    }
  };

  const [formData, setFormData] = useState<QuestionFormData>({
    subject: 'mathematics',
    topic: '',
    difficulty: 'medium',
    questionText: '',
    questionImage: '',
    optionA: '',
    optionB: '',
    optionC: '',
    optionD: '',
    correctAnswer: 'A',
    explanation: '',
    explanationImage: '',
    marks: 4,
    negativeMarks: -1,
    year: null,
    source: '',
    tags: []
  });

  useEffect(() => {
    fetchQuestions();
  }, [currentPage, searchTerm, filters]);

  useEffect(() => {
    if (formData.subject !== 'all') {
      fetchTopics(formData.subject);
    }
  }, [formData.subject]);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 20,
        search: searchTerm,
        ...(filters.subject !== 'all' && { subject: filters.subject }),
        ...(filters.topic !== 'all' && { topic: filters.topic }),
        ...(filters.difficulty !== 'all' && { difficulty: filters.difficulty }),
        ...(filters.year !== 'all' && { year: filters.year }),
        ...(filters.isActive !== 'all' && { isActive: filters.isActive })
      };

      const response = await adminAPI.getQuestions(params);
      if (response.data.success) {
        // Handle response structure - data is the array directly, pagination is separate
        const questionsData = response.data.data || [];
        const paginationData = response.data.pagination || { pages: 1 };
        
        setQuestions(questionsData);
        setTotalPages(paginationData.pages);
        setTotalCount(paginationData.total ?? questionsData.length);
      }
    } catch (error: any) {
      toast.error('Failed to load questions');
      console.error('Fetch questions error:', error);
      // Set empty array on error to prevent undefined errors
      setQuestions([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const fetchTopics = async (subject: string) => {
    try {
      const response = await adminAPI.getTopics(subject);
      if (response.data.success) {
        setTopics(response.data.data);
      }
    } catch (error) {
      console.error('Fetch topics error:', error);
    }
  };

  const handleCreateQuestion = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      const response = await adminAPI.createQuestion(formData);
      if (response.data.success) {
        toast.success('Question created successfully!');
        setShowCreateModal(false);
        resetForm();
        fetchQuestions();
        fetchStats();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to create question');
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateQuestion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedQuestion) return;

    setFormLoading(true);
    try {
      const response = await adminAPI.updateQuestion(selectedQuestion.id.toString(), formData);
      if (response.data.success) {
        toast.success('Question updated successfully!');
        setShowEditModal(false);
        setSelectedQuestion(null);
        resetForm();
        fetchQuestions();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to update question');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteQuestion = async (questionId: number) => {
    if (!confirm('Are you sure you want to delete this question? This action cannot be undone.')) {
      return;
    }

    try {
      await adminAPI.deleteQuestion(questionId.toString());
      toast.success('Question deleted successfully!');
      fetchQuestions();
      fetchStats();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to delete question');
    }
  };

  const toggleQuestionStatus = async (questionId: number, currentStatus: boolean) => {
    try {
      await adminAPI.updateQuestion(questionId.toString(), { isActive: !currentStatus });
      toast.success(`Question ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      fetchQuestions();
    } catch (error: any) {
      toast.error('Failed to update question status');
    }
  };

  const resetForm = () => {
    setFormData({
      subject: 'mathematics',
      topic: '',
      difficulty: 'medium',
      questionText: '',
      questionImage: '',
      optionA: '',
      optionB: '',
      optionC: '',
      optionD: '',
      correctAnswer: 'A',
      explanation: '',
      explanationImage: '',
      marks: 4,
      negativeMarks: -1,
      year: null,
      source: '',
      tags: []
    });
  };

  const openEditModal = (question: Question) => {
    setSelectedQuestion(question);
    setFormData({
      subject: question.subject,
      topic: question.topic,
      difficulty: question.difficulty,
      questionText: question.questionText,
      questionImage: question.questionImage || '',
      optionA: question.optionA,
      optionB: question.optionB,
      optionC: question.optionC,
      optionD: question.optionD,
      correctAnswer: question.correctAnswer,
      explanation: question.explanation || '',
      explanationImage: question.explanationImage || '',
      marks: question.marks,
      negativeMarks: question.negativeMarks,
      year: question.year || null,
      source: question.source || '',
      tags: question.tags
    });
    setShowEditModal(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty.toLowerCase()) {
      case 'easy': return 'success';
      case 'medium': return 'warning';
      case 'hard': return 'danger';
      default: return 'secondary';
    }
  };

  const getSubjectColor = (subject: string) => {
    switch (subject.toLowerCase()) {
      case 'mathematics': return 'primary';
      case 'physics': return 'success';
      case 'chemistry': return 'warning';
      default: return 'secondary';
    }
  };

  const filteredQuestions = (questions || []).filter(question => {
    const matchesSearch = question.questionText.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         question.topic.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         question.explanation?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSubject = filters.subject === 'all' || question.subject === filters.subject;
    const matchesTopic = filters.topic === 'all' || question.topic.includes(filters.topic);
    const matchesDifficulty = filters.difficulty === 'all' || question.difficulty === filters.difficulty;
    const matchesYear = filters.year === 'all' || question.year?.toString() === filters.year;
    const matchesStatus = filters.isActive === 'all' || 
                         (filters.isActive === 'active' && question.isActive) ||
                         (filters.isActive === 'inactive' && !question.isActive);
    
    return matchesSearch && matchesSubject && matchesTopic && matchesDifficulty && matchesYear && matchesStatus;
  });

  const addTag = (tag: string) => {
    if (tag && !formData.tags.includes(tag)) {
      setFormData({ ...formData, tags: [...formData.tags, tag] });
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData({ ...formData, tags: formData.tags.filter(tag => tag !== tagToRemove) });
  };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-2">Question Bank</h1>
            <p className="text-sm md:text-base text-secondary-600">Create and manage questions for mock tests</p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center px-4 py-2.5 md:py-2 text-sm font-medium text-white bg-primary-600 border border-primary-600 rounded-lg hover:bg-primary-700 transition-colors touch-target"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Question
            </button>
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mb-6 md:mb-8">
        <div className="stats-card">
          <div className="flex items-center justify-between mb-3 md:mb-4">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
              <BookOpen className="h-5 w-5 md:h-6 md:w-6 text-white" />
            </div>
          </div>
          <div className="stats-value">{stats.total}</div>
          <div className="stats-label">Total Questions</div>
        </div>

        <div className="stats-card">
          <div className="flex items-center justify-between mb-3 md:mb-4">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r from-success-500 to-success-600 rounded-xl flex items-center justify-center">
              <CheckCircle className="h-5 w-5 md:h-6 md:w-6 text-white" />
            </div>
          </div>
          <div className="stats-value">{stats.active}</div>
          <div className="stats-label">Active Questions</div>
        </div>

        <div className="stats-card">
          <div className="flex items-center justify-between mb-3 md:mb-4">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r from-warning-500 to-warning-600 rounded-xl flex items-center justify-center">
              <Target className="h-5 w-5 md:h-6 md:w-6 text-white" />
            </div>
          </div>
          <div className="stats-value">{stats.hard}</div>
          <div className="stats-label">Hard Questions</div>
        </div>

        <div className="stats-card">
          <div className="flex items-center justify-between mb-3 md:mb-4">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r from-accent-500 to-accent-600 rounded-xl flex items-center justify-center">
              <BarChart3 className="h-5 w-5 md:h-6 md:w-6 text-white" />
            </div>
          </div>
          <div className="stats-value">{stats.topics}</div>
          <div className="stats-label">Topics Covered</div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card mb-4 md:mb-6">
        <div className="card-content">
          <div className="flex flex-col space-y-3 md:space-y-4">
            <div className="flex items-center space-x-2 md:space-x-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
                <input
                  type="text"
                  placeholder="Search questions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2.5 md:py-2 w-full border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm md:text-base"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 md:flex md:flex-wrap items-center gap-2 md:gap-4">
              <select
                value={filters.subject}
                onChange={(e) => sets({ ...filters, subject: e.target.value })}
                className="px-3 py-2.5 md:py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              >
                <option value="all">All Subjects</option>
                <option value="mathematics">Mathematics</option>
                <option value="physics">Physics</option>
                <option value="chemistry">Chemistry</option>
              </select>
              
              <select
                value={filters.difficulty}
                onChange={(e) => sets({ ...filters, difficulty: e.target.value })}
                className="px-3 py-2.5 md:py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              >
                <option value="all">All Difficulties</option>
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
              
              <select
                value={filters.isActive}
                onChange={(e) => sets({ ...filters, isActive: e.target.value })}
                className="px-3 py-2.5 md:py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>

              <input
                type="number"
                placeholder="Year"
                value={filters.year === 'all' ? '' : filters.year}
                onChange={(e) => sets({ ...filters, year: e.target.value || 'all' })}
                className="px-3 py-2.5 md:py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-full md:w-24 text-sm"
              />
              
              <div className="col-span-2 flex items-center justify-center md:justify-start space-x-2 text-xs md:text-sm text-secondary-600 pt-2 md:pt-0">
                <BookOpen className="h-4 w-4" />
                <span>{filteredQuestions.length} questions found</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Questions Grid */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary-500 border-t-transparent mx-auto mb-4"></div>
            <p className="text-secondary-600">Loading questions...</p>
          </div>
        ) : filteredQuestions.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-secondary-900 mb-2">No Questions Found</h3>
            <p className="text-secondary-600 mb-4">
              {searchTerm || Object.values(filters).some(f => f !== 'all')
                ? 'No questions match your search criteria.' 
                : 'Get started by creating your first question.'
              }
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-primary-600 rounded-lg hover:bg-primary-700 transition-colors mx-auto"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Question
            </button>
          </div>
        ) : (
          filteredQuestions.map((question) => (
            <div key={question.id} className="card-interactive group">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between p-4 md:p-6 gap-4">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className={`badge badge-${getSubjectColor(question.subject)} text-[10px] md:text-xs`}>
                      {question.subject.charAt(0).toUpperCase() + question.subject.slice(1)}
                    </span>
                    <span className={`badge badge-${getDifficultyColor(question.difficulty)} text-[10px] md:text-xs`}>
                      {question.difficulty}
                    </span>
                    <span className={`badge badge-${question.isActive ? 'success' : 'secondary'} text-[10px] md:text-xs`}>
                      {question.isActive ? 'Active' : 'Inactive'}
                    </span>
                    {question.year && (
                      <span className="badge badge-secondary text-[10px] md:text-xs">
                        {question.year}
                      </span>
                    )}
                  </div>
                  
                  <div className="mb-3">
                    <h3 className="text-xs md:text-sm font-medium text-secondary-700 mb-1">Topic: {question.topic}</h3>
                    <p className="text-sm md:text-base text-secondary-900 leading-relaxed line-clamp-2">
                      {question.questionText}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3 md:gap-4 mb-3 md:mb-4">
                    <div className="flex items-center text-xs md:text-sm text-secondary-600">
                      <Target className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-2" />
                      {question.marks} marks
                    </div>
                    <div className="flex items-center text-xs md:text-sm text-secondary-600">
                      <XCircle className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-2" />
                      {question.negativeMarks} negative
                    </div>
                  </div>

                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                    <div className="flex items-center space-x-2">
                      {question.tags.length > 0 && (
                        <div className="flex items-center space-x-1 flex-wrap gap-1">
                          <Tag className="h-3 w-3 text-secondary-400" />
                          {question.tags.slice(0, 3).map((tag) => (
                            <span key={tag} className="badge badge-secondary badge-sm text-[9px] md:text-[10px]">
                              {tag}
                            </span>
                          ))}
                          {question.tags.length > 3 && (
                            <span className="text-[10px] md:text-xs text-secondary-500">
                              +{question.tags.length - 3} more
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <div className="text-xs md:text-sm text-secondary-500">
                      Created: {formatDate(question.createdAt)}
                    </div>
                  </div>
                </div>

                <div className="flex md:flex-col items-center md:items-end gap-2 md:gap-3 md:ml-6">
                  <div className="flex items-center gap-1 md:gap-2">
                    <button
                      onClick={() => {
                        setSelectedQuestion(question);
                        setShowQuestionDetails(true);
                      }}
                      className="p-2 md:p-2 text-secondary-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors touch-target-sm"
                      title="View Details"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => openEditModal(question)}
                      className="p-2 md:p-2 text-secondary-600 hover:text-warning-600 hover:bg-warning-50 rounded-lg transition-colors touch-target-sm"
                      title="Edit Question"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => toggleQuestionStatus(question.id, question.isActive)}
                      className={`p-2 md:p-2 rounded-lg transition-colors touch-target-sm ${
                        question.isActive 
                          ? 'text-secondary-600 hover:text-danger-600 hover:bg-danger-50' 
                          : 'text-secondary-600 hover:text-success-600 hover:bg-success-50'
                      }`}
                      title={question.isActive ? 'Deactivate' : 'Activate'}
                    >
                      {question.isActive ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                    </button>
                    <button
                      onClick={() => handleDeleteQuestion(question.id)}
                      className="p-2 md:p-2 text-secondary-600 hover:text-danger-600 hover:bg-danger-50 rounded-lg transition-colors touch-target-sm"
                      title="Delete Question"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="text-right hidden md:block">
                    <div className="text-xs md:text-sm text-secondary-500">Correct Answer</div>
                    <div className="text-base md:text-lg font-semibold text-secondary-900">
                      Option {question.correctAnswer}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6 md:mt-8 flex-wrap">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="px-3 py-2 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-lg hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed touch-target-sm"
          >
            Previous
          </button>
          
          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const page = i + 1;
              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-2 text-sm font-medium rounded-lg touch-target-sm ${
                    currentPage === page
                      ? 'text-white bg-primary-600'
                      : 'text-secondary-700 bg-white border border-secondary-300 hover:bg-secondary-50'
                  }`}
                >
                  {page}
                </button>
              );
            })}
          </div>
          
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-2 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-lg hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed touch-target-sm"
          >
            Next
          </button>
        </div>
      )}

      {/* Create/Edit Question Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 md:p-4">
          <div className="bg-white rounded-xl md:rounded-2xl max-w-4xl w-full max-h-[95vh] md:max-h-[90vh] overflow-y-auto">
            <div className="p-4 md:p-6 border-b border-secondary-200 sticky top-0 bg-white z-10">
              <div className="flex items-center justify-between">
                <h3 className="text-lg md:text-xl font-semibold text-secondary-900">
                  {showCreateModal ? 'Create New Question' : 'Edit Question'}
                </h3>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                    setSelectedQuestion(null);
                    resetForm();
                  }}
                  className="text-secondary-400 hover:text-secondary-600 touch-target-sm"
                >
                  <X className="h-5 w-5 md:h-6 md:w-6" />
                </button>
              </div>
            </div>
            
            <form onSubmit={showCreateModal ? handleCreateQuestion : handleUpdateQuestion} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Basic Information */}
                <div className="md:col-span-2">
                  <h4 className="text-lg font-semibold text-secondary-900 mb-4">Basic Information</h4>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Subject *
                  </label>
                  <select
                    required
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="mathematics">Mathematics</option>
                    <option value="physics">Physics</option>
                    <option value="chemistry">Chemistry</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Topic *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.topic}
                    onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter topic name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Difficulty *
                  </label>
                  <select
                    required
                    value={formData.difficulty}
                    onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Year
                  </label>
                  <input
                    type="number"
                    min="2000"
                    max="2030"
                    value={formData.year || ''}
                    onChange={(e) => setFormData({ ...formData, year: e.target.value ? parseInt(e.target.value) : null })}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="e.g., 2023"
                  />
                </div>

                {/* Question Content */}
                <div className="md:col-span-2">
                  <h4 className="text-lg font-semibold text-secondary-900 mb-4 mt-6">Question Content</h4>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Question Text *
                  </label>
                  <textarea
                    required
                    rows={4}
                    value={formData.questionText}
                    onChange={(e) => setFormData({ ...formData, questionText: e.target.value })}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter the question text"
                  />
                </div>

                {/* Options */}
                <div className="md:col-span-2">
                  <h4 className="text-lg font-semibold text-secondary-900 mb-4">Answer Options</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      { key: 'A', field: 'optionA' },
                      { key: 'B', field: 'optionB' },
                      { key: 'C', field: 'optionC' },
                      { key: 'D', field: 'optionD' }
                    ].map(({ key, field }) => (
                      <div key={key}>
                        <label className="block text-sm font-medium text-secondary-700 mb-2">
                          Option {key} *
                        </label>
                        <textarea
                          required
                          rows={2}
                          value={formData[field as keyof QuestionFormData] as string}
                          onChange={(e) => setFormData({
                            ...formData,
                            [field]: e.target.value
                          })}
                          className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          placeholder={`Enter option ${key}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Correct Answer *
                  </label>
                  <select
                    required
                    value={formData.correctAnswer}
                    onChange={(e) => setFormData({ ...formData, correctAnswer: e.target.value })}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="A">Option A</option>
                    <option value="B">Option B</option>
                    <option value="C">Option C</option>
                    <option value="D">Option D</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Marks
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={formData.marks}
                    onChange={(e) => setFormData({ ...formData, marks: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* Explanation */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Explanation
                  </label>
                  <textarea
                    rows={4}
                    value={formData.explanation}
                    onChange={(e) => setFormData({ ...formData, explanation: e.target.value })}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Explain why this is the correct answer"
                  />
                </div>

                {/* Tags */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Tags
                  </label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {formData.tags.map((tag) => (
                      <span key={tag} className="badge badge-primary flex items-center">
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="ml-1 text-primary-600 hover:text-primary-800"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <input
                    type="text"
                    placeholder="Add tags (press Enter)"
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const target = e.target as HTMLInputElement;
                        addTag(target.value.trim());
                        target.value = '';
                      }
                    }}
                  />
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex items-center justify-end space-x-3 mt-8 pt-6 border-t border-secondary-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                    setSelectedQuestion(null);
                    resetForm();
                  }}
                  className="px-4 py-2 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-lg hover:bg-secondary-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formLoading}
                  className="flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {formLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                      {showCreateModal ? 'Creating...' : 'Updating...'}
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      {showCreateModal ? 'Create Question' : 'Update Question'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Question Details Modal */}
      {showQuestionDetails && selectedQuestion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-secondary-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-secondary-900">Question Details</h3>
                <button
                  onClick={() => setShowQuestionDetails(false)}
                  className="text-secondary-400 hover:text-secondary-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="space-y-6">
                {/* Question Info */}
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className={`badge badge-${getSubjectColor(selectedQuestion.subject)}`}>
                    {selectedQuestion.subject.charAt(0).toUpperCase() + selectedQuestion.subject.slice(1)}
                  </span>
                  <span className={`badge badge-${getDifficultyColor(selectedQuestion.difficulty)}`}>
                    {selectedQuestion.difficulty}
                  </span>
                  <span className="badge badge-secondary">
                    Topic: {selectedQuestion.topic}
                  </span>
                  {selectedQuestion.year && (
                    <span className="badge badge-secondary">
                      Year: {selectedQuestion.year}
                    </span>
                  )}
                  <span className="badge badge-secondary">
                    {selectedQuestion.marks} marks / {selectedQuestion.negativeMarks} negative
                  </span>
                </div>

                {/* Question Text */}
                <div>
                  <h4 className="text-lg font-semibold text-secondary-900 mb-3">Question</h4>
                  <div className="bg-secondary-50 p-4 rounded-lg">
                    <p className="text-secondary-900 leading-relaxed">{selectedQuestion.questionText}</p>
                  </div>
                </div>

                {/* Options */}
                <div>
                  <h4 className="text-lg font-semibold text-secondary-900 mb-3">Options</h4>
                  <div className="space-y-3">
                    {[
                      { key: 'A', value: selectedQuestion.optionA },
                      { key: 'B', value: selectedQuestion.optionB },
                      { key: 'C', value: selectedQuestion.optionC },
                      { key: 'D', value: selectedQuestion.optionD }
                    ].map(({ key, value }) => (
                      <div
                        key={key}
                        className={`p-3 rounded-lg border-2 ${
                          key === selectedQuestion.correctAnswer
                            ? 'border-success-300 bg-success-50'
                            : 'border-secondary-200 bg-white'
                        }`}
                      >
                        <div className="flex items-start space-x-3">
                          <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold ${
                            key === selectedQuestion.correctAnswer
                              ? 'bg-success-600 text-white'
                              : 'bg-secondary-200 text-secondary-700'
                          }`}>
                            {key}
                          </span>
                          <p className="text-secondary-900 flex-1">{value}</p>
                          {key === selectedQuestion.correctAnswer && (
                            <CheckCircle className="h-5 w-5 text-success-600" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Explanation */}
                {selectedQuestion.explanation && (
                  <div>
                    <h4 className="text-lg font-semibold text-secondary-900 mb-3">Explanation</h4>
                    <div className="bg-primary-50 p-4 rounded-lg">
                      <p className="text-secondary-900 leading-relaxed">{selectedQuestion.explanation}</p>
                    </div>
                  </div>
                )}

                {/* Tags */}
                {selectedQuestion.tags.length > 0 && (
                  <div>
                    <h4 className="text-lg font-semibold text-secondary-900 mb-3">Tags</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedQuestion.tags.map((tag) => (
                        <span key={tag} className="badge badge-primary">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Metadata */}
                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-secondary-200">
                  <div>
                    <h5 className="text-sm font-medium text-secondary-700">Created</h5>
                    <p className="text-secondary-900">{formatDate(selectedQuestion.createdAt)}</p>
                  </div>
                  <div>
                    <h5 className="text-sm font-medium text-secondary-700">Last Updated</h5>
                    <p className="text-secondary-900">{formatDate(selectedQuestion.updatedAt)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}